import { forwardRef, useState, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Select,
  MultiSelect,
  TextInput,
  Checkbox,
  Paper,
  ActionIcon,
  Menu,
  Tooltip,
  Badge,
  Tabs,
  Divider,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconPlus,
  IconTrash,
  IconGripVertical,
  IconFilter,
  IconSortAscending,
  IconChartBar,
  IconTable,
  IconDownload,
  IconRefresh,
  IconSettings,
  IconEye,
  IconDeviceFloppy,
} from '@tabler/icons-react';
import { Button, Card } from '../../ui';
import { FadeIn } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface ReportField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select';
  options?: { value: string; label: string }[];
  aggregations?: ('sum' | 'avg' | 'count' | 'min' | 'max')[];
}

export interface ReportFilter {
  field: string;
  operator: 'equals' | 'contains' | 'gt' | 'lt' | 'gte' | 'lte' | 'between' | 'in';
  value: unknown;
}

export interface ReportSort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ReportConfig {
  name: string;
  description?: string;
  fields: string[];
  filters: ReportFilter[];
  groupBy?: string[];
  sortBy?: ReportSort[];
  aggregations?: { field: string; type: string }[];
  visualization?: 'table' | 'bar' | 'line' | 'pie' | 'area';
  dateRange?: { start: Date; end: Date };
}

export interface ReportBuilderProps {
  availableFields: ReportField[];
  initialConfig?: Partial<ReportConfig>;
  onConfigChange?: (config: ReportConfig) => void;
  onGenerate?: (config: ReportConfig) => void;
  onSave?: (config: ReportConfig) => void;
  onExport?: (format: 'csv' | 'excel' | 'pdf') => void;
  savedReports?: { id: string; name: string }[];
  onLoadReport?: (id: string) => void;
  loading?: boolean;
  previewData?: Record<string, unknown>[];
  className?: string;
}

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'gt', label: 'Greater than' },
  { value: 'lt', label: 'Less than' },
  { value: 'gte', label: 'Greater or equal' },
  { value: 'lte', label: 'Less or equal' },
  { value: 'between', label: 'Between' },
  { value: 'in', label: 'In list' },
];

export const ReportBuilder = forwardRef<HTMLDivElement, ReportBuilderProps>(
  (
    {
      availableFields,
      initialConfig = {},
      onConfigChange,
      onGenerate,
      onSave,
      onExport,
      savedReports = [],
      onLoadReport,
      loading = false,
      previewData = [],
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [config, setConfig] = useState<ReportConfig>({
      name: 'New Report',
      fields: [],
      filters: [],
      groupBy: [],
      sortBy: [],
      aggregations: [],
      visualization: 'table',
      ...initialConfig,
    });

    const [activeTab, setActiveTab] = useState<string | null>('fields');

    const updateConfig = (updates: Partial<ReportConfig>) => {
      const newConfig = { ...config, ...updates };
      setConfig(newConfig);
      onConfigChange?.(newConfig);
    };

    const addFilter = () => {
      const newFilter: ReportFilter = {
        field: availableFields[0]?.key || '',
        operator: 'equals',
        value: '',
      };
      updateConfig({ filters: [...config.filters, newFilter] });
    };

    const updateFilter = (index: number, updates: Partial<ReportFilter>) => {
      const newFilters = [...config.filters];
      newFilters[index] = { ...newFilters[index], ...updates };
      updateConfig({ filters: newFilters });
    };

    const removeFilter = (index: number) => {
      updateConfig({ filters: config.filters.filter((_, i) => i !== index) });
    };

    const addSort = () => {
      const newSort: ReportSort = {
        field: availableFields[0]?.key || '',
        direction: 'asc',
      };
      updateConfig({ sortBy: [...(config.sortBy || []), newSort] });
    };

    const removeSort = (index: number) => {
      updateConfig({ sortBy: config.sortBy?.filter((_, i) => i !== index) });
    };

    const getFieldLabel = (key: string) => {
      return availableFields.find((f) => f.key === key)?.label || key;
    };

    return (
      <FadeIn>
        <Card ref={ref} className={className} p={0}>
          <Stack gap={0}>
            {/* Header */}
            <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
              <Group justify="space-between">
                <Group gap="sm">
                  <TextInput
                    placeholder="Report name"
                    value={config.name}
                    onChange={(e) => updateConfig({ name: e.target.value })}
                    style={{ width: 200 }}
                  />
                  {savedReports.length > 0 && (
                    <Select
                      placeholder="Load saved report"
                      data={savedReports.map((r) => ({ value: r.id, label: r.name }))}
                      onChange={(value) => value && onLoadReport?.(value)}
                      clearable
                      style={{ width: 180 }}
                    />
                  )}
                </Group>
                <Group gap="sm">
                  {onSave && (
                    <Button
                      variant="subtle"
                      leftSection={<IconDeviceFloppy size={16} />}
                      onClick={() => onSave(config)}
                    >
                      Save
                    </Button>
                  )}
                  {onExport && (
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <Button variant="subtle" leftSection={<IconDownload size={16} />}>
                          Export
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => onExport('csv')}>CSV</Menu.Item>
                        <Menu.Item onClick={() => onExport('excel')}>Excel</Menu.Item>
                        <Menu.Item onClick={() => onExport('pdf')}>PDF</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                  {onGenerate && (
                    <Button
                      leftSection={<IconEye size={16} />}
                      onClick={() => onGenerate(config)}
                      loading={loading}
                    >
                      Generate Report
                    </Button>
                  )}
                </Group>
              </Group>
            </Box>

            {/* Configuration */}
            <Box style={{ display: 'flex' }}>
              <Box style={{ width: 300, borderRight: '1px solid var(--mantine-color-gray-3)' }}>
                <Tabs value={activeTab} onChange={setActiveTab} orientation="vertical">
                  <Tabs.List>
                    <Tabs.Tab value="fields" leftSection={<IconTable size={16} />}>
                      Fields
                    </Tabs.Tab>
                    <Tabs.Tab value="filters" leftSection={<IconFilter size={16} />}>
                      Filters
                      {config.filters.length > 0 && (
                        <Badge size="xs" ml={4}>{config.filters.length}</Badge>
                      )}
                    </Tabs.Tab>
                    <Tabs.Tab value="sorting" leftSection={<IconSortAscending size={16} />}>
                      Sorting
                    </Tabs.Tab>
                    <Tabs.Tab value="grouping" leftSection={<IconChartBar size={16} />}>
                      Grouping
                    </Tabs.Tab>
                    <Tabs.Tab value="visualization" leftSection={<IconSettings size={16} />}>
                      Visualization
                    </Tabs.Tab>
                  </Tabs.List>
                </Tabs>
              </Box>

              <Box style={{ flex: 1 }} p="md">
                {activeTab === 'fields' && (
                  <Stack gap="md">
                    <Text size="sm" fw={500} style={accessibilityStyles}>Select fields to include in the report</Text>
                    <MultiSelect
                      label="Report Fields"
                      placeholder="Select fields"
                      data={availableFields.map((f) => ({ value: f.key, label: f.label }))}
                      value={config.fields}
                      onChange={(value) => updateConfig({ fields: value })}
                      searchable
                    />
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>
                      Drag to reorder fields in the report
                    </Text>
                    <Stack gap="xs">
                      {config.fields.map((field, index) => (
                        <Paper key={field} p="xs" withBorder>
                          <Group gap="sm">
                            <IconGripVertical size={14} color="gray" style={{ cursor: 'grab' }} />
                            <Text size="sm" style={{ flex: 1, ...accessibilityStyles }}>{getFieldLabel(field)}</Text>
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              color="red"
                              onClick={() => updateConfig({ fields: config.fields.filter((f) => f !== field) })}
                            >
                              <IconTrash size={12} />
                            </ActionIcon>
                          </Group>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                )}

                {activeTab === 'filters' && (
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text size="sm" fw={500} style={accessibilityStyles}>Filter conditions</Text>
                      <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<IconPlus size={14} />}
                        onClick={addFilter}
                      >
                        Add Filter
                      </Button>
                    </Group>
                    {config.filters.map((filter, index) => (
                      <Paper key={index} p="sm" withBorder>
                        <Group gap="sm">
                          <Select
                            placeholder="Field"
                            data={availableFields.map((f) => ({ value: f.key, label: f.label }))}
                            value={filter.field}
                            onChange={(value) => value && updateFilter(index, { field: value })}
                            style={{ width: 150 }}
                          />
                          <Select
                            placeholder="Operator"
                            data={OPERATORS}
                            value={filter.operator}
                            onChange={(value) => value && updateFilter(index, { operator: value as ReportFilter['operator'] })}
                            style={{ width: 130 }}
                          />
                          <TextInput
                            placeholder="Value"
                            value={String(filter.value)}
                            onChange={(e) => updateFilter(index, { value: e.target.value })}
                            style={{ flex: 1 }}
                          />
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => removeFilter(index)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Paper>
                    ))}
                    {config.filters.length === 0 && (
                      <Text size="sm" c="dimmed" ta="center" py="md" style={accessibilityStyles}>
                        No filters applied
                      </Text>
                    )}
                  </Stack>
                )}

                {activeTab === 'sorting' && (
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text size="sm" fw={500} style={accessibilityStyles}>Sort order</Text>
                      <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<IconPlus size={14} />}
                        onClick={addSort}
                      >
                        Add Sort
                      </Button>
                    </Group>
                    {config.sortBy?.map((sort, index) => (
                      <Paper key={index} p="sm" withBorder>
                        <Group gap="sm">
                          <Select
                            placeholder="Field"
                            data={availableFields.map((f) => ({ value: f.key, label: f.label }))}
                            value={sort.field}
                            onChange={(value) => {
                              if (!value) return;
                              const newSortBy = [...(config.sortBy || [])];
                              newSortBy[index] = { ...sort, field: value };
                              updateConfig({ sortBy: newSortBy });
                            }}
                            style={{ flex: 1 }}
                          />
                          <Select
                            data={[
                              { value: 'asc', label: 'Ascending' },
                              { value: 'desc', label: 'Descending' },
                            ]}
                            value={sort.direction}
                            onChange={(value) => {
                              if (!value) return;
                              const newSortBy = [...(config.sortBy || [])];
                              newSortBy[index] = { ...sort, direction: value as 'asc' | 'desc' };
                              updateConfig({ sortBy: newSortBy });
                            }}
                            style={{ width: 130 }}
                          />
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => removeSort(index)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                )}

                {activeTab === 'grouping' && (
                  <Stack gap="md">
                    <Text size="sm" fw={500} style={accessibilityStyles}>Group by fields</Text>
                    <MultiSelect
                      label="Group By"
                      placeholder="Select fields to group by"
                      data={availableFields.map((f) => ({ value: f.key, label: f.label }))}
                      value={config.groupBy}
                      onChange={(value) => updateConfig({ groupBy: value })}
                    />
                    <Divider label="Aggregations" labelPosition="left" />
                    <Text size="xs" c="dimmed" style={accessibilityStyles}>
                      Select aggregation functions for numeric fields
                    </Text>
                    {availableFields
                      .filter((f) => f.type === 'number')
                      .map((field) => (
                        <Group key={field.key} gap="sm">
                          <Text size="sm" style={{ width: 120, ...accessibilityStyles }}>{field.label}</Text>
                          <MultiSelect
                            placeholder="Aggregations"
                            data={[
                              { value: 'sum', label: 'Sum' },
                              { value: 'avg', label: 'Average' },
                              { value: 'count', label: 'Count' },
                              { value: 'min', label: 'Min' },
                              { value: 'max', label: 'Max' },
                            ]}
                            value={
                              config.aggregations
                                ?.filter((a) => a.field === field.key)
                                .map((a) => a.type) || []
                            }
                            onChange={(types) => {
                              const otherAggregations = config.aggregations?.filter((a) => a.field !== field.key) || [];
                              const newAggregations = types.map((type) => ({ field: field.key, type }));
                              updateConfig({ aggregations: [...otherAggregations, ...newAggregations] });
                            }}
                            style={{ flex: 1 }}
                          />
                        </Group>
                      ))}
                  </Stack>
                )}

                {activeTab === 'visualization' && (
                  <Stack gap="md">
                    <Text size="sm" fw={500} style={accessibilityStyles}>Visualization type</Text>
                    <Select
                      label="Chart Type"
                      data={[
                        { value: 'table', label: 'Table' },
                        { value: 'bar', label: 'Bar Chart' },
                        { value: 'line', label: 'Line Chart' },
                        { value: 'pie', label: 'Pie Chart' },
                        { value: 'area', label: 'Area Chart' },
                      ]}
                      value={config.visualization}
                      onChange={(value) => value && updateConfig({ visualization: value as ReportConfig['visualization'] })}
                    />
                    <Divider label="Date Range" labelPosition="left" />
                    <Group grow>
                      <DatePickerInput
                        label="Start Date"
                        value={config.dateRange?.start}
                        onChange={(date) =>
                          date && updateConfig({
                            dateRange: { ...config.dateRange, start: date, end: config.dateRange?.end || date },
                          })
                        }
                        clearable
                      />
                      <DatePickerInput
                        label="End Date"
                        value={config.dateRange?.end}
                        onChange={(date) =>
                          date && updateConfig({
                            dateRange: { ...config.dateRange, end: date, start: config.dateRange?.start || date },
                          })
                        }
                        clearable
                      />
                    </Group>
                  </Stack>
                )}
              </Box>
            </Box>

            {/* Preview */}
            {previewData.length > 0 && (
              <Box p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
                <Text size="sm" fw={500} mb="sm" style={accessibilityStyles}>Preview ({previewData.length} rows)</Text>
                <Box style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {config.fields.map((field) => (
                          <th
                            key={field}
                            style={{
                              padding: '8px',
                              textAlign: 'left',
                              borderBottom: '1px solid var(--mantine-color-gray-3)',
                            }}
                          >
                            <Text size="sm" fw={500} style={accessibilityStyles}>{getFieldLabel(field)}</Text>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 5).map((row, index) => (
                        <tr key={index}>
                          {config.fields.map((field) => (
                            <td
                              key={field}
                              style={{
                                padding: '8px',
                                borderBottom: '1px solid var(--mantine-color-gray-2)',
                              }}
                            >
                              <Text size="sm" style={accessibilityStyles}>{String(row[field] ?? '-')}</Text>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            )}
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

ReportBuilder.displayName = 'ReportBuilder';

export default ReportBuilder;
